import { RadioButtonGroup } from "@heroui-pro/react/radio-button-group";
import { memo, useEffect, useRef, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

export type ScenarioOption = {
  id: string;
  label: string;
  description?: string;
  emoji?: string;
  imageUrl?: string;
  moodEmoji?: string;
};

type ScenarioPickerProps = {
  question: string;
  description?: string;
  scenarios: ScenarioOption[];
  initialValue?: string;
  columns?: 2 | 3;
  autoAdvance?: boolean;
  onAnswer: (scenarioId: string) => void;
  className?: string;
  testId?: string;
};

const AUTO_ADVANCE_MS = 280;

const ScenarioPickerImpl = ({
  question,
  description,
  scenarios,
  initialValue,
  columns = 2,
  autoAdvance = true,
  onAnswer,
  className,
  testId,
}: ScenarioPickerProps) => {
  const [value, setValue] = useState<string | undefined>(initialValue);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    [],
  );

  const handleChange = (next: string): void => {
    setValue(next);
    if (!autoAdvance) {
      onAnswer(next);
      return;
    }
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => onAnswer(next), AUTO_ADVANCE_MS);
  };

  const gridClass = columns === 3 ? "grid grid-cols-3 gap-3" : "grid grid-cols-2 gap-3";

  return (
    <section data-testid={testId} className={`flex flex-col gap-6 ${className ?? ""}`}>
      <StepHeading title={question} description={description} editablePath="question" />
      <RadioButtonGroup
        aria-label={question}
        className={gridClass}
        layout="grid"
        name={`scenario-${testId ?? "default"}`}
        value={value ?? null}
        onChange={handleChange}
      >
        {scenarios.map((scenario) => (
          <RadioButtonGroup.Item
            key={scenario.id}
            value={scenario.id}
            className="flex-col gap-2 overflow-hidden p-3"
          >
            <RadioButtonGroup.ItemContent className="flex-col items-center gap-2 p-0 text-center">
              {scenario.imageUrl ? (
                <div className="relative w-full overflow-hidden rounded-lg">
                  <ResponsiveAsset
                    src={scenario.imageUrl}
                    alt=""
                    loading="eager"
                    className="aspect-[4/3] w-full object-cover"
                  />
                  {scenario.moodEmoji && (
                    <span className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-black/40 text-base backdrop-blur">
                      {scenario.moodEmoji}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-4xl">{scenario.emoji ?? "🎭"}</span>
              )}
              <span className="text-sm font-semibold">{scenario.label}</span>
              {scenario.description && (
                <span className="text-xs" style={{ color: "hsl(var(--hero-fg-muted))" }}>
                  {scenario.description}
                </span>
              )}
            </RadioButtonGroup.ItemContent>
          </RadioButtonGroup.Item>
        ))}
      </RadioButtonGroup>
    </section>
  );
};

export const ScenarioPicker = memo(ScenarioPickerImpl);

export default ScenarioPicker;
